import { ReactNode, useState } from 'react';
import { PHONE, useMediaQuery } from '../../shared/media';

/**
 * The strip the admin banners sit in (D1, docs/PLAYBOOK-mobile.md §4D).
 *
 * On a desktop this is exactly the `<div className="bl-banner-wrap">` it
 * replaces — same element, same class, no extra child — so nothing about the
 * banners moves. On a phone the strip starts collapsed: every banner keeps its
 * pill and shows one line of its sentence, the actions are out of the way, and
 * one "More" button below the strip opens all of them at once.
 *
 * One toggle for the strip rather than one per banner: the two places that
 * render banners (AdminLayout's announcement and notice, TrialBanner's five
 * shapes) build their own `.bl-banner` markup, and a shared attribute on the
 * wrapper collapses all of them without touching any of it. The collapse
 * itself is CSS keyed on `data-collapsed`; the button is the only new DOM, and
 * it exists only while the phone query matches.
 *
 * Dismissal is unchanged: it belongs to whoever owns the banner, and a
 * dismissed banner never reaches this component.
 */
export default function BannerStrip({ children }: { children: ReactNode }) {
  const phone = useMediaQuery(PHONE);
  const [open, setOpen] = useState(false);
  const collapsed = phone && !open;

  return (
    <div className="bl-banner-wrap" data-collapsed={collapsed ? 'true' : undefined}>
      {children}
      {phone && (
        <button type="button" className="bl-more" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'Less' : 'More'}
        </button>
      )}
    </div>
  );
}
